import { BACKGROUNDS, teamColorsOn } from '@/utils/colors'

// Shot chart helpers shared by the match page chart and the live-stream card.
// Shot coordinates are normalised 0..1 on a half court with the hoop at the top centre.

const ASSETS_URL = 'https://storage.googleapis.com'

// Older shots kept the top-left corner of the 24px mark of the stats app (on a 550px court), not the spot itself
const MARK_OFFSET = 12 / 550

// The spot of a stored shot (0..1): the newer ones (centered) are the spot of the tap already
export function shotSpot(shot) {
    const shift = shot && shot.centered ? 0 : MARK_OFFSET
    return { x: Number(shot.x) + shift, y: Number(shot.y) + shift }
}

// Geometry of assets/halfcourt.jpg, in percent of the image (0..100)
export const HOOP = { x: 50, y: 0 }
export const ARC_RADIUS = 43.1
export const PAINT = { left: 40.4, right: 59.5, bottom: 28.7 }

const point = (radius, degrees) => {
    const rad = degrees * Math.PI / 180
    return `${(HOOP.x + radius * Math.cos(rad)).toFixed(2)} ${(HOOP.y + radius * Math.sin(rad)).toFixed(2)}`
}

// inside the arc, between two angles (0° = right baseline, 180° = left baseline)
const insideSector = (from, to) => `M${HOOP.x} ${HOOP.y} L${point(ARC_RADIUS, from)} A${ARC_RADIUS} ${ARC_RADIUS} 0 0 1 ${point(ARC_RADIUS, to)} Z`
// outside the arc, up to the edges of the court
const outsideSector = (from, to) => `M${point(ARC_RADIUS, from)} A${ARC_RADIUS} ${ARC_RADIUS} 0 0 1 ${point(ARC_RADIUS, to)} L${point(200, to)} L${point(200, from)} Z`

export const PAINT_PATH = `M${PAINT.left} 0 H${PAINT.right} V${PAINT.bottom} H${PAINT.left} Z`

// family decides the colour limits; `at` is where the badge sits (percent)
export const ZONES = [
    { id: 'paint', label: 'Ρακέτα', family: 'paint', path: PAINT_PATH, at: [50, 14] },
    { id: 'mid-right', label: 'Μέση απόσταση δεξιά', family: 'mid', from: 0, to: 60, masked: true, path: insideSector(0, 60), at: [74, 14] },
    { id: 'mid-center', label: 'Μέση απόσταση κέντρο', family: 'mid', from: 60, to: 120, masked: true, path: insideSector(60, 120), at: [50, 36] },
    { id: 'mid-left', label: 'Μέση απόσταση αριστερά', family: 'mid', from: 120, to: 180, masked: true, path: insideSector(120, 180), at: [26, 14] },
    { id: 'corner-right', label: 'Τρίποντο γωνία δεξιά', family: 'three', from: 0, to: 22, path: outsideSector(0, 22), at: [92, 16] },
    { id: 'wing-right', label: 'Τρίποντο πλάγια δεξιά', family: 'three', from: 22, to: 68, path: outsideSector(22, 68), at: [87, 40] },
    { id: 'top', label: 'Τρίποντο κορυφή', family: 'three', from: 68, to: 112, path: outsideSector(68, 112), at: [50, 60] },
    { id: 'wing-left', label: 'Τρίποντο πλάγια αριστερά', family: 'three', from: 112, to: 158, path: outsideSector(112, 158), at: [13, 40] },
    { id: 'corner-left', label: 'Τρίποντο γωνία αριστερά', family: 'three', from: 158, to: 180, path: outsideSector(158, 180), at: [8, 16] }
]

// [good, average] field goal percentage of each family
const ZONE_LIMITS = {
    paint: [55, 45],
    mid: [42, 34],
    three: [36, 28]
}

// The zone of a shot comes from where it was taken, so it always matches the borders drawn on the court
export function getShotZone(shot) {
    if (getShotCategory(shot) === 'ft') return null
    const x = shot.x * 100
    const y = shot.y * 100
    if (x >= PAINT.left && x <= PAINT.right && y <= PAINT.bottom) return 'paint'
    const distance = Math.hypot(x - HOOP.x, y - HOOP.y)
    const angle = Math.atan2(Math.max(0, y - HOOP.y), x - HOOP.x) * 180 / Math.PI
    const family = distance <= ARC_RADIUS ? 'mid' : 'three'
    const zone = ZONES.find(item => item.family === family && angle >= item.from && angle <= item.to)
    return zone ? zone.id : null
}

export function getZoneLevel(zone, made, attempts) {
    const [good, average] = ZONE_LIMITS[zone.family]
    const pct = attempts ? (made / attempts) * 100 : 0
    if (pct >= good) return 'hot'
    if (pct >= average) return 'ok'
    return 'cold'
}

export function toPercentLabel(made, attempts) {
    if (!attempts) return '0%'
    return `${Math.round((made / attempts) * 100)}%`
}

export function getShotCategory(shot = {}) {
    const points = Number(shot.points)
    if (points === 1) return 'ft'
    if (points === 3) return 'three'
    if (points === 2) return 'two'
    return null
}

export function getTeamColor(team = {}, fallback = '#ef7f33') {
    const raw = team?.color || team?.primaryColor || team?.teamColor || team?.hexColor || team?.mainColor || null
    if (!raw) return fallback

    const normalized = String(raw).trim()
    if (!normalized) return fallback
    if (/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return normalized
    if (/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return `#${normalized}`
    if (/^rgb\(|^hsl\(/i.test(normalized)) return normalized

    return fallback
}

// The two teams' colours, each visible on the background it is drawn on and told apart from the other
export function getTeamColors(team1, team2, background = BACKGROUNDS.white, minContrast) {
    return teamColorsOn(getTeamColor(team1, null), getTeamColor(team2, null), background, minContrast)
}

export function periodLabel(period) {
    return period <= 4 ? `${period}η` : `Παρ. ${period - 4}`
}

function getPlayerStatsEntries(player = {}) {
    return Array.isArray(player?.stats) ? player.stats.filter(stat => stat && typeof stat === 'object') : []
}

export function getPlayerKey(player = {}, index = 0) {
    const id = player.id || player.playerId || player.athleteId || player?.player?.id || player?.athlete?.id
    if (id !== null && id !== undefined && String(id).trim() !== '') {
        return String(id)
    }

    const jersey = player.jersey ?? player.number ?? player?.player?.jersey ?? player?.athlete?.jersey ?? ''
    const name =
        player.name ||
        player.player ||
        player.playerName ||
        `${player.firstName || ''} ${player.lastName || ''}`.trim() ||
        `Player ${index + 1}`

    return `${String(jersey).trim()}-${String(name).trim().toLowerCase()}`
}

function getPlayerLabel(player = {}, index = 0) {
    const jersey = player.jersey ?? player.number ?? null
    const lastName = player.surname || player.lastName || player.lastname || ''
    const base = lastName || player.name || player.player || `Παίκτης ${index + 1}`
    return jersey !== null && jersey !== undefined && `${jersey}` !== '' ? `#${jersey} ${base}` : base
}

export function getPlayerImageSrc(player = {}, fallback = null) {
    const raw = player.image || player.photo || player.avatar || player.imageUrl || null
    if (!raw) return fallback
    if (/^https?:\/\//i.test(raw)) return raw
    return `${ASSETS_URL}/${raw}`
}

const TRACKED_FIELDS = [
    'points', 'rebounds', 'assists', 'steals', 'turnovers', 'blockedShots', 'personalFouls', 'twoPointsMade',
    'threePointsMade', 'freeThrowsMade', 'twoPointAttempts', 'threePointAttempts', 'freeThrowAttempts', 'pir'
]

export function hasPlayedMatch(player = {}) {
    return getPlayerStatsEntries(player).some((stat) => {
        if (Array.isArray(stat.throwPositions) && stat.throwPositions.length > 0) return true
        if ((Number(stat.playTimeInSecs) || 0) > 0) return true
        return TRACKED_FIELDS.some(field => (Number(stat[field]) || 0) > 0)
    })
}

// players who took part, sorted by jersey
export function buildPlayerList(players = [], defaultImage = null) {
    return (Array.isArray(players) ? players : [])
        .map((player, index) => ({ player, index }))
        .filter(({ player }) => hasPlayedMatch(player))
        .map(({ player, index }) => {
            const jersey = player.jersey ?? player.number ?? null
            return {
                key: getPlayerKey(player, index),
                number: Number.isFinite(Number(jersey)) && `${jersey}` !== '' ? Number(jersey) : Number.POSITIVE_INFINITY,
                label: getPlayerLabel(player, index),
                imageSrc: getPlayerImageSrc(player, defaultImage)
            }
        })
        .sort((a, b) => (a.number - b.number) || a.label.localeCompare(b.label))
}

function periodFromElapsedSeconds(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return null
    if (seconds <= 2400) return Math.max(1, Math.ceil(seconds / 600))
    return 4 + Math.ceil((seconds - 2400) / 300)
}

export function resolveShotPeriod(shot = {}) {
    const text = typeof shot.shotTimestamp === 'string' ? shot.shotTimestamp.trim() : ''
    const mmss = text.match(/^(\d{1,3}):(\d{2})$/)
    if (!mmss) return null
    const mm = Number(mmss[1])
    const ss = Number(mmss[2])
    if (ss >= 60) return null
    return periodFromElapsedSeconds((mm * 60) + ss)
}

export function getShotPoints(shot = {}) {
    const value = Number(shot.value)
    return Number.isFinite(value) && value > 0 ? value : 2
}

export function buildShots(players = [], teamKey, color) {
    return (Array.isArray(players) ? players : []).flatMap((player, playerIndex) => {
        const playerKey = getPlayerKey(player, playerIndex)
        const playerName = getPlayerLabel(player, playerIndex)

        return getPlayerStatsEntries(player).flatMap((stat, statIndex) => {
            const positions = Array.isArray(stat?.throwPositions) ? stat.throwPositions : []
            return positions
                .map((shot, shotIndex) => {
                    const { x, y } = shotSpot(shot)
                    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
                    return {
                        id: `${teamKey}-${playerKey}-${statIndex}-${shotIndex}`,
                        teamKey,
                        playerKey,
                        playerName,
                        x: Math.max(0, Math.min(1, x)),
                        y: Math.max(0, Math.min(1, y)),
                        made: shot.throwStatus === 'made',
                        period: resolveShotPeriod(shot),
                        points: getShotPoints(shot),
                        color
                    }
                })
                .filter(Boolean)
        })
    })
}

// made / attempts of 2 + 3 pointers, 2 pointers and 3 pointers
export function summarizeShots(shots = []) {
    const totals = { two: { made: 0, attempts: 0 }, three: { made: 0, attempts: 0 } }
    shots.forEach((shot) => {
        const category = getShotCategory(shot)
        if (!totals[category]) return
        totals[category].attempts += 1
        if (shot.made) totals[category].made += 1
    })
    return {
        fg: { made: totals.two.made + totals.three.made, attempts: totals.two.attempts + totals.three.attempts },
        two: totals.two,
        three: totals.three
    }
}
